import {Answer} from "../../interfaces/answer";

/**
 * Represents an answer or message associated with a support ticket.
 * This class implements the `Answer` interface and includes properties
 * such as title, message content, author, associated customer and ticket IDs,
 * as well as any relevant attachments.
 */
class TicketAnswer implements Answer {

    public title: string;
    public message: string;
    private readonly customerId: string;
    private readonly ticketId: string;
    private readonly author: string;
    private attachments: File[];

    /**
     * Creates an instance of the constructor with specified properties for a support ticket or message.
     *
     * @param {string} title - The title or subject of the message or ticket.
     * @param {string} message - The content or body of the message.
     * @param {string} customerId - The unique identifier of the customer related to the ticket.
     * @param {string} ticketId - The unique identifier of the ticket.
     * @param {string} author - The name or identifier of the author of the message.
     * @param {File[]} attachments - A list of attachments associated with the message.
     */
    constructor(
        title: string,
        message: string,
        customerId: string,
        ticketId: string,
        author: string,
        attachments: File[]
    ) {
        this.title = title;
        this.message = message;
        this.customerId = customerId;
        this.ticketId = ticketId;
        this.author = author;
        this.attachments = attachments;
    }

    /**
     * Gets the title of the ticket answer
     * @returns {string} The title
     */
    public getTitle(): string {
        return this.title;
    }

    /**
     * Gets the message content of the ticket answer
     * @returns {string} The message
     */
    public getMessage(): string {
        return this.message;
    }

    /**
     * Gets the customer ID associated with the ticket answer
     * @returns {string} The customer ID
     */
    public getCustomerId(): string {
        return this.customerId;
    }

    /**
     * Gets the ticket ID associated with the answer
     * @returns {string} The ticket ID
     */
    public getTicketId(): string {
        return this.ticketId;
    }

    /**
     * Gets the author of the ticket answer
     * @returns {string} The author
     */
    public getAuthor(): string {
        return this.author;
    }

    /**
     * Gets the attachments associated with the ticket answer
     * @returns {File[]} The array of attachments
     */
    public getAttachments(): File[] {
        return this.attachments;
    }

    /**
     * Retrieves the first attachment from the list of attachments.
     *
     * @return {File} The first attachment in the list.
     */
    public getAttachment(): File {
        return this.attachments[0];
    }

    /**
     * Determines if the current object contains any attachments.
     *
     * @return {boolean} Returns true if attachments exist, otherwise false.
     */
    public hasAttachement(): boolean {
        return this.attachments?.length > 0;
    }

    /**
     * Adds an attachment to the current ticket answer.
     *
     * @param {File} attachment - The attachment object to be added.
     * @return {TicketAnswer} The current TicketAnswer instance with the updated list of attachments.
     */
    public addAttachment(attachment: File): TicketAnswer {
        this.attachments?.push(attachment);
        return this;
    }

}

export default TicketAnswer;